<template>
  <div class="notification-btn">
    <v-menu
      v-model="showNotifications"
      offset-y
      :close-on-content-click="false"
      rounded
      width="660px"
      max-height="600px"
    >
      <template #activator="{ on, attrs }">
        <v-badge
          :content="unseenNotificationsCount"
          :value="unseenNotificationsCount"
          overlap
          class="notification-badge notification-badge--support"
        >
          <v-btn
            v-bind="attrs"
            class="bar-button button-notifications text-normalize"
            height="60"
            depressed
            v-on="on"
          >
            <font-awesome-icon color="#FF9100" icon="bell" />
            <span class="button-notifications-label">Уведомления</span>
          </v-btn>
        </v-badge>
      </template>
      <notification-list
        :loading="isLoading"
        :reading="isReading"
        :notifications="notifications"
        @read="readSupport"
        @open-support="openSupport"
      />
    </v-menu>
    <primary-confirm-dialog
      v-model="showConfirmDialog"
      v-bind="confirmDialog"
      :loading="dialogLoading"
      @confirm="confirmAction"
    />
  </div>
</template>

<script>
  import { mapGetters, mapState } from 'vuex'
  import notificationAPI from '@/api/notification'
  import fiscalShiftApi from '@/api/administrator/fiscal-shift'
  import { autoSettlementNotice } from '@/services/terminal-settlement'
  import snackbarMessage from '@/mixins/snackbar-message'
  import { websocketService } from '@/services/WebsocketService'
  import { getFilialChannel, getUserChannel } from '@/util/websocket-channels'
  import {
    CRM_NOTIFICATION_CREATED,
    CRM_NOTIFICATION_UPDATED,
  } from '@/constants/websocket-events'
  import NotificationList from './NotificationList.vue'
  import NotificationLevel from '@/models/enums/notification/NotificationLevel'
  import NotificationType from '@/models/enums/notification/NotificationType'
  import CRMPermission from '@/models/enums/permissions/CRMPermission'
  import PrimaryConfirmDialog from '../ui/dialogs/PrimaryConfirmDialog.vue'
  import permissions from '@/mixins/permissions'
  import { Howl } from 'howler'
  import { refreshService } from '@/services/refresh'

  export default {
    name: 'NotificationButton',
    components: { NotificationList, PrimaryConfirmDialog },
    mixins: [snackbarMessage, permissions],
    data () {
      return {
        showNotifications: false,
        isLoading: false,
        confirmDialog: {},
        confirmAction: () => {},
        showConfirmDialog: false,
        dialogLoading: false,
        notifications: [],
        isReading: false,
        orderCanceledSound: null,
      }
    },
    computed: {
      ...mapGetters({
        localStorageBranch: 'branches/getLocalStorageBranch',
        getUser: 'auth/getUser',
        supportEnabled: 'support/enabled',
      }),
      ...mapState({
        unseenNotificationsCount: (state) =>
          state.notifications.unseenNotificationsCount,
        refreshTick: (state) => state.notifications.refreshTick,
      }),
      canViewNotifications () {
        return this.userCan(CRMPermission.ViewNotifications)
      },
    },
    watch: {
      showNotifications (value) {
        if (value && this.canViewNotifications) {
          this.$store.dispatch(
            'notifications/viewNotifications',
            this.notifications,
          )
        }
      },
      // Страница тикета поддержки погасила уведомления на сервере - бейдж должен уменьшиться.
      // fakt-support гасит их в CRM уже после ответа на /read (defer), поэтому перечитываем
      // с запасом в 1.5 с, а не сразу.
      refreshTick () {
        if (!this.canViewNotifications) return
        clearTimeout(this.refreshTimer)
        this.refreshTimer = setTimeout(() => this.fetchNotifications(), 1500)
      },
    },
    created () {
      if (this.canViewNotifications) {
        this.orderCanceledSound = new Howl({
          src: ['/sounds/cancel_2.mp3'],
        })
        this.fetchNotifications()
        this.addNotificationListeners()
        refreshService.register(this.refreshNotifications)
      }
    },
    beforeDestroy () {
      clearTimeout(this.refreshTimer)
      this.removeNotificationListeners()
      refreshService.unregister(this.refreshNotifications)
    },
    methods: {
      addNotificationListeners () {
        if (process.env.VUE_APP_MODE === 'local') {
          console.log('------PUBLIC FILIAL CHANEL------')
          websocketService
            .getEcho()
            .channel(getFilialChannel(this.localStorageBranch.id))
            .listen(CRM_NOTIFICATION_CREATED, this.notificationCreatedListener)
            .listen(CRM_NOTIFICATION_UPDATED, this.notificationUpdatedListener)

          if (this.getUser?.id) {
            websocketService
              .getEcho()
              .channel(getUserChannel(this.getUser.id))
              .listen(CRM_NOTIFICATION_CREATED, this.notificationCreatedListener)
              .listen(CRM_NOTIFICATION_UPDATED, this.notificationUpdatedListener)
          }
        } else {
          websocketService
            .getEcho()
            .private(getFilialChannel(this.localStorageBranch.id))
            .listen(CRM_NOTIFICATION_CREATED, this.notificationCreatedListener)
            .listen(CRM_NOTIFICATION_UPDATED, this.notificationUpdatedListener)

          if (this.getUser?.id) {
            websocketService
              .getEcho()
              .private(getUserChannel(this.getUser.id))
              .listen(CRM_NOTIFICATION_CREATED, this.notificationCreatedListener)
              .listen(CRM_NOTIFICATION_UPDATED, this.notificationUpdatedListener)
          }
        }
      },
      removeNotificationListeners () {
        if (process.env.VUE_APP_MODE === 'local') {
          websocketService
            .getEcho()
            .channel(getFilialChannel(this.localStorageBranch.id))
            .stopListening(CRM_NOTIFICATION_CREATED, this.notificationCreatedListener)
            .stopListening(CRM_NOTIFICATION_UPDATED, this.notificationUpdatedListener)

          if (this.getUser?.id) {
            websocketService
              .getEcho()
              .channel(getUserChannel(this.getUser.id))
              .stopListening(
                CRM_NOTIFICATION_CREATED,
                this.notificationCreatedListener,
              )
              .stopListening(
                CRM_NOTIFICATION_UPDATED,
                this.notificationUpdatedListener,
              )
          }
        } else {
          websocketService
            .getEcho()
            .private(getFilialChannel(this.localStorageBranch.id))
            .stopListening(CRM_NOTIFICATION_CREATED, this.notificationCreatedListener)
            .stopListening(CRM_NOTIFICATION_UPDATED, this.notificationUpdatedListener)

          if (this.getUser?.id) {
            websocketService
              .getEcho()
              .private(getUserChannel(this.getUser.id))
              .stopListening(
                CRM_NOTIFICATION_CREATED,
                this.notificationCreatedListener,
              )
              .stopListening(
                CRM_NOTIFICATION_UPDATED,
                this.notificationUpdatedListener,
              )
          }
        }
      },
      notificationUpdatedListener (notification) {
        if (Object.keys(this.confirmDialog).length && !notification.isActive) {
          this.showConfirmDialog = false
        }

        this.fetchNotifications()
      },
      async fetchNotifications ({ silent = false } = {}) {
        this.isLoading = true
        try {
          const response = await notificationAPI.fetchNotifications(
            this.localStorageBranch.id,
          )
          const items = response.items.slice()
          // ВРЕМЕННО: филиал вне VUE_APP_SUPPORT_BRANCH_IDS - уведомлений поддержки в списке нет
          // (support/enabled). Бейдж считается из этого же списка в calcNotificationsCount.
          this.notifications = this.supportEnabled
            ? items
            : items.filter(item => !NotificationType.isSupport(item.type))
          this.$store.dispatch(
            'notifications/calcNotificationsCount',
            this.notifications,
          )

          if (this.showNotifications) {
            this.$store.dispatch(
              'notifications/viewNotifications',
              this.notifications,
            )
          }
        } catch (e) {
          if (!silent) {
            this.showErrorFromResponse(e)
          }
        } finally {
          this.isLoading = false
        }
      },
      refreshNotifications ({ silent }) {
        return this.fetchNotifications({ silent })
      },
      notificationCreatedListener (notification) {
        console.log('*************** NOTIFICATION ***************')
        console.log(notification)

        if (notification.type === NotificationType.OrderCanceled) {
          this.orderCanceledSound.play()
        }

        // Ответ поддержки показывает тостом виджет чата (T08), второй снекбар CRM не нужен.
        if (NotificationType.isSupport(notification.type)) {
          this.fetchNotifications()
          return
        }

        if (notification.showModal) {
          this.showDialog(notification)
        } else {
          switch (notification.level) {
            case NotificationLevel.Error:
              this.showErrorMessage(notification.title)
              break
            case NotificationLevel.Warning:
              this.showWarningMessage(notification.title)
              break
            default:
              this.showInfoMessage(notification.title)
          }
        }

        this.fetchNotifications()
      },
      // «Прочитано» у строки (ids) или «Прочитать все» (null) - только уведомления поддержки.
      async readSupport (ids) {
        if (this.isReading) return
        this.isReading = true
        try {
          await notificationAPI.readNotifications(this.localStorageBranch.id, ids)
          await this.fetchNotifications()
        } catch (e) {
          this.showErrorFromResponse(e)
        } finally {
          this.isReading = false
        }
      },
      // Тикет филиала - страница webcrm /support/:id (T09); прочитанным уведомление
      // становится там, при открытии тикета, и колокольчик перечитывается по refreshTick.
      openSupport (notification) {
        const appealId = notification.payload?.appealId
        if (!appealId) return
        this.showNotifications = false

        const path = `/support/${appealId}`
        if (this.$route.path !== path) {
          this.$router.push(path)
        }
      },
      resetConfirmDialog () {
        this.confirmDialog = {}
        this.confirmAction = () => {}
      },
      showDialog (notification) {
        this.resetConfirmDialog()

        switch (notification.type) {
          case NotificationType.WorkShiftNotClosed:
            this.confirmDialog = {
              notificationId: notification.id,
              title: notification.title,
              subtitle: 'Хотите закрыть смену?',
            }
            this.confirmAction = this.closeFiscalShift
            break
          case NotificationType.FiscalCheckPrintFailed:
          case NotificationType.PrintCheckFailed:
            this.confirmDialog = {
              notificationId: notification.id,
              title: notification.title,
              subtitle: notification.description,
              confirmText: 'Посмотреть',
              cancelText: 'Продолжить',
            }
            this.confirmAction = this.goToFiscalization
            break
          case NotificationType.OrderCanceled:
            this.confirmDialog = {
              notificationId: notification.id,
              title: notification.title,
              confirmText: 'Посмотреть',
              cancelText: 'Закрыть',
            }
            this.confirmAction = this.openOrder(notification.orderId)
            break
          default:
            this.confirmDialog = {
              notificationId: notification.id,
              title: notification.title,
              subtitle: notification.description,
              cancelText: 'Продолжить',
              hideConfirmBtn: true,
            }
        }

        this.showConfirmDialog = true
      },
      async closeFiscalShift () {
        if (!this.userCan(CRMPermission.WorkShift)) {
          this.showErrorMessage('Доступ запрещен')
          return
        }

        this.dialogLoading = true
        try {
          const response = await fiscalShiftApi.close(this.localStorageBranch.id)
          const notice = autoSettlementNotice(response)

          if (!notice) {
            this.showSuccessMessage('Смена успешно закрыта')
          } else if (notice.isError) {
            this.showErrorMessage(notice.text)
          } else {
            this.showSuccessMessage(`Смена закрывается. ${notice.text}`)
          }
          this.showConfirmDialog = false
        } catch (e) {
          this.showErrorFromResponse(e)
        } finally {
          this.dialogLoading = false
        }
      },
      goToFiscalization () {
        const route = this.$router.resolve({
          name: 'cashier',
          query: { tab: 'fiscalization' },
        }).route

        if (this.$route.fullPath !== route.fullPath) {
          this.$router.push(route)
        }

        this.showConfirmDialog = false
      },
      openOrder (orderId) {
        return () => {
          const route = this.$router.resolve({
            name: 'fastOrderEdit',
            query: { id: orderId },
          }).route

          if (this.$route.fullPath !== route.fullPath) {
            this.$router.push(route)
          }

          this.showConfirmDialog = false
        }
      },
    },
  }
</script>

<style lang="less" scoped>
@import '../support/tokens.less';

.notification-badge {
  margin-right: 10px;
  z-index: 1;

  // Все бейджи пользователя розовые (FAKT Support, 11-acceptance.md)
  &--support ::v-deep .v-badge__badge {
    background-color: @fakt-pink !important;
  }

  .media-compact-pc({
    margin-right: 6px;
  });
}

.bar-button {
  height: 50px;
  font-size: 14px;
  background-color: @color-secondary !important;
  border-radius: 5px;

  .media-xl({
      //margin-right: 20px;
    });
}

//   .app-bar-component {
//     .button-notifications {
//       display: none;
//       margin-bottom: 10px;
//       width: 100%;
//     }
//   }

.button-notifications {
  svg {
    margin-right: 0;
  }
  span {
    margin-left: 15px;
  }

  @media (max-width: 1903px) {
    min-width: 60px;
    svg {
      margin-right: 0;
    }
  }

  // до 1600px (шире мобильной раскладки): квадратная кнопка с одной иконкой.
  // С подписью шапке нужно ~1575px (сайдбар 260 + кнопки + семь метрик + аватар),
  // поэтому на 1501-1600 с подписью аватар уходил за край экрана
  @media (min-width: (@lg + 1)) and (max-width: 1600px) {
    width: 60px;
    min-width: 60px !important;
    padding: 0 !important;
  }

  .button-notifications-label {
    @media (min-width: 2160px) {
      display: inline-block;
    }
    @media (min-width: (@lg + 1)) and (max-width: 1600px) {
      display: none;
    }
    .media-md({
      display: none;
    });
    .media-sm({
      display: inline;
    });
  }
}
</style>
